import { when, computed } from 'mobx';
import { MobileErrorCode, SaveType } from '@tencent/mega-sdk-protocols';
import { DocumentStatus } from '@tencent/slide-sdk-model/src/manager/content-status-manager';
import { MobileUIState } from '@src/slide/infrastructure/module/independent/mobile/mobile-ui-state';
import { UIState } from '@src/slide/infrastructure/module/independent/ui-state';
import { browser, isAppLargerOrEqual } from '@src/slide/infrastructure/module/independent/agent/common';
import { RenderLayerService } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/thumbnail';
import { DataConvertError } from '../../../core/interfaces/local-edit-data-converter.interface';
import { BaseLocalEditManager } from '../../../core/managers/local-edit-manager.base';
import { MobileLocalEditAPI } from '../services/local-edit-api.mobile';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import {
    IMobileEditorAppServices,
} from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import Snackbar from '@tencent/dui-mobile/lib/components/Snackbar';
const DefaultRenderPostionType = 'list';
const DefaultRenderScale = 1;
const EXIT_EDIT_DELAY_TIME = 300;
const SSR_PAGE = 3; // 后台固定返回3页，前端页数可做调整
const TEXT_MAX_LENGTH = 20; // 二三页筛选掉文本长度过长的情况，一般来说标题的字数比较少，较长的是字数比较多的描述性文字
const SIZE_MIN = 60;    // uiModel上的宽高值，二三页小于改值不做渲染

export class MobileLocalEditManager extends BaseLocalEditManager {
    private api = new MobileLocalEditAPI(window.mqq);

    protected init() {
        this.api.registerCustomEvents({
            onSaveData: ({ actionType, saveType }: { actionType: string, saveType: SaveType }) => {
                if (this.contentStatusManager.getDocumentStatus() === DocumentStatus.Sync) {
                    // 延迟退出编辑态 防止拉起文件路径选择前出现页面跳变
                    setTimeout(() => {
                        this.getEntryStateService()?.exitEditMode?.();
                    }, EXIT_EDIT_DELAY_TIME);
                    return;
                }

                this.save(saveType)
                    .then(() => {
                        this.api.notifySaveResult({ actionType, success: true });
                        this.getEntryStateService()?.exitEditMode?.();
                    })
                    .catch(() => {
                        this.api.notifySaveResult({ actionType, success: false });
                    });
            },
            onPictureShare: () => {
                this.getExportImageManager()?.exportImage({ appShare: true });
            },
            onPresentation: ({ actionType, onFinish }: { actionType: string, onFinish: () => void }) => {
                if (actionType === 'start') {
                    this.getOpenClientApiManager()?.playPresentation({ onFinish });
                } else if (actionType === 'exit') {
                    this.getOpenClientApiManager()?.exitPresentation();
                }
            },
            getNewLocalFileDefaultName: async () => {
                // 获取默认标题前主动触发失焦，确保所有编辑内容都被保存
                this.getEditorInstance()?.triggerBlur();
                await new Promise((resolve => setTimeout(resolve, 0)));
                const newTitle = await this.context.getSaveTitle();
                this.api.sendNewLocalFileDefaultName(newTitle);
            },
            exitEditMode: () => {
                this.getEntryStateService()?.exitEditMode?.();
            },
        });

        when(
            () => this.hideNoticebar,
            () => this.api.closeUploadNoticebar(),
        );
    }

    protected notifyResult(err?: DataConvertError) {
        if (err) {
            this.api.notifyLoadResult({
                errorCode: err.code ?? MobileErrorCode.ErrorUnknown,
                errorMsg: err.message,
            });
        } else {
            this.api.notifyLoadResult({ errorCode: 0, errorMsg: '' });
        }
    }

    // 获取首屏 ssr 渲染数据
    protected getSsrData() {
        // 安卓 3.8.0 及以上版本支持
        if (!(browser.isAndroid && isAppLargerOrEqual('3.8.0'))) {
            return '';
        }
        const appService = this.getAppServices();
        const uiDataService = appService.getUiDataService();
        const selectionService = appService.viewableServiceLayer?.selectionService;
        const slideCount = selectionService?.slides?.length;
        if (slideCount === 0) {
            return '';
        }

        const { slideWidth, slideHeight } = uiDataService.presentation.props;
        const { viewableServiceLayer } = appService;
        const getSlideRenderData = (
            slideModel: ISlideModel, notRenderTypeList?: UIModelType[],
            shouldFilterText?: boolean,
        ) => {
            const filteredModel = this.deepTraverseUiModelAndFilter(slideModel, notRenderTypeList, shouldFilterText);
            const layerService = new RenderLayerService(
                // @ts-ignore
                filteredModel,
                // @ts-ignore
                null,
                DefaultRenderScale,
                () => DefaultRenderScale,
                DefaultRenderPostionType,
                false,
                false,
                appService.getInfrastructureService(),
                uiDataService,
                undefined,
                undefined,
                selectionService,
                viewableServiceLayer,
                undefined,
            );
            const renderData = layerService.getApplication().collectRawRenderData();
            layerService?.destroyLayer?.();
            return renderData;
        };
        const { children } = uiDataService.presentation;
        const firstSlideRenderData = children.slice(0, 1).map(child => getSlideRenderData(child));
        // 此处可以传入二三页想要过滤的类型
        const restPageSlideRenderData = children.slice(1, Math.min(SSR_PAGE, children.length))
            .map(model => getSlideRenderData(model, [], true));
        const canvas = children.map((item, index) => `<canvas id="slide-canvas-${index}" class="pre-page-canvas"></canvas>`).join('');
        const renderData = {
            slideInfo: { slideWidth, slideHeight, slideCount },
            slideRenderData: [...firstSlideRenderData, ...restPageSlideRenderData],
        };
        const canvasSsrVersion = '20240827-1500';
        return `<div id="preTempSlide"><div id="temp-slide-area"></div></div><div id="previewSlide" class="pre-page-slide-bg">${canvas}</div><script>renderData = ${JSON.stringify(renderData)};ssrVersion='canvas-${canvasSsrVersion}';</script>`;
    }

    protected getAppServices() {
        return AppServiceProvider.getInstance().getAppServices() as IMobileEditorAppServices;
    }

    protected getSnackbBar() {
        return Snackbar;
    }

    private deepTraverseUiModelAndFilter(
        model: IElementBaseModel | ISlideModel, notRenderTypeList?: UIModelType[],
        shouldFilterText?: boolean,
    ) {
        if (!this.shouleRenderElement(model, notRenderTypeList, shouldFilterText)) return null;
        // @ts-ignore
        if (model.children && model.children.length > 0) {
            // @ts-ignore
            model.children = model.children
                .map(child => this.deepTraverseUiModelAndFilter(child, notRenderTypeList, shouldFilterText))
                .filter(child => child !== null);
        }
        return model;
    }

    private shouleRenderElement(
        model: IElementBaseModel | ISlideModel, notRenderTypeList: UIModelType[] = [],
        shouldFilterText?: boolean,
    ) {
        if (model.type === UIModelType.SLIDE) return true;
        const elementModel = model as IElementBaseModel;
        if ([UIModelType.IMAGE, ...notRenderTypeList].includes(elementModel.type)) return false;
        if (typeof elementModel.props.style.width === 'number' && elementModel.props.style.width < SIZE_MIN) {
            return false;
        }
        if (typeof elementModel.props.style.height === 'number' && elementModel.props.style.height < SIZE_MIN) {
            return false;
        }
        if (shouldFilterText) {
            // @ts-ignore
            const textLength = elementModel.paragraphList?.reduce((acc, prev) => {
                const textRunsLength = prev.textRuns.reduce((acc, prev) => prev.text.length + acc, 0);
                return textRunsLength + acc;
            }, 0) ?? 0;
            if (textLength > TEXT_MAX_LENGTH) return false;
        }
        return true;
    }

    private getEntryStateService() {
        return this.getAppServices().viewableServiceLayer.entryStateService;
    }

    private getExportImageManager() {
        return this.getAppServices().viewableServiceLayer.exportImageManager;
    }

    private getOpenClientApiManager() {
        return this.getAppServices().viewableServiceLayer.openClientApiManager;
    }
    /**
     * 切换场景主动关闭顶部上云 noticebar
     */
    @computed
    private get hideNoticebar() {
        const { isPlaying, isEditing } = MobileUIState;
        const { isExportingToImage } = UIState;
        return isEditing || isPlaying || isExportingToImage;
    }
}
